import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { desktop } from '@/lib/printTransport'
import { parseReceiptSettings } from '@/lib/printCustomerReceipt'
import { buildDesktopAppIconPng } from '@/lib/desktopAppIcon'

/**
 * In the generic desktop build, sets the window/taskbar icon to the tenant's
 * configured receipt logo — giving each restaurant a branded app. The main
 * process ignores this on baked single-tenant builds (which keep their own
 * baked icon, e.g. the Bhookly demo). No-op in a normal browser.
 */
export function DesktopBranding() {
  const d = desktop()

  const settingsQuery = useQuery({
    queryKey: ['settings', 'all'],
    queryFn: () => apiClient.getAllSettings(),
    enabled: !!d,
  })

  useEffect(() => {
    if (!d || !settingsQuery.data) return
    const cfg = parseReceiptSettings(
      (settingsQuery.data.data as Record<string, unknown>) ?? undefined,
    )
    const logo = (cfg.logoUrl ?? '').trim()
    if (!logo) return

    // Rasterise to a square PNG before handing it over. Passing the raw URL is
    // what this used to do, and it silently did nothing for any tenant whose
    // logo was an SVG — nativeImage cannot decode one, and the main process
    // treats an empty image as "keep the current icon". Wide banner logos also
    // came out squashed. See lib/desktopAppIcon.ts.
    let cancelled = false
    void (async () => {
      const png = await buildDesktopAppIconPng(logo)
      if (!cancelled && png) void d.setAppIcon(png)
    })()
    return () => {
      cancelled = true
    }
  }, [d, settingsQuery.data])

  return null
}
